import { Extension, Mark, mergeAttributes } from '@tiptap/core';
import Paragraph from '@tiptap/extension-paragraph';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { mintParaId } from '../model/materialise';

export interface EditorStyleInfo { name: string; fontId: string; pointSize: number; leading: number; fillHex: string }

export interface StyledParagraphOptions {
  styles: Record<string, EditorStyleInfo>;
  /** px per pt in the panel */
  scale: number;
  HTMLAttributes: Record<string, unknown>;
}

/** Paragraph node carrying paraId and styleId, rendered in the paragraph style's face so the client sees what they edit. */
export const StyledParagraph = Paragraph.extend<StyledParagraphOptions>({
  addOptions() {
    return { styles: {}, scale: 1, HTMLAttributes: {} };
  },
  addAttributes() {
    return {
      paraId: { default: null, parseHTML: (el) => el.getAttribute('data-para-id'), renderHTML: (a) => (a.paraId ? { 'data-para-id': a.paraId } : {}) },
      styleId: { default: null, parseHTML: (el) => el.getAttribute('data-style-id'), renderHTML: (a) => (a.styleId ? { 'data-style-id': a.styleId } : {}) },
    };
  },
  renderHTML({ node, HTMLAttributes }) {
    const st = this.options.styles[node.attrs.styleId as string];
    const px = st ? Math.max(10, Math.min(40, st.pointSize * this.options.scale)) : 14;
    const lh = st ? Math.max(px * 1.15, st.leading * this.options.scale) : px * 1.4;
    const style = st ? `font-family:"${st.fontId}",${'Helvetica Neue'},Helvetica,sans-serif;font-size:${px.toFixed(2)}px;line-height:${lh.toFixed(2)}px;color:${st.fillHex || '#141414'}` : '';
    return ['p', mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, { 'data-style-name': st?.name ?? node.attrs.styleId ?? '', style }), 0];
  },
});

/** Comment thread range. `excludes: ''` lets threads overlap. */
export const CommentMark = Mark.create<{ activeThreadId?: string; resolved: Record<string, boolean> }>({
  name: 'comment',
  inclusive: false,
  excludes: '',
  addOptions() { return { activeThreadId: undefined, resolved: {} }; },
  addAttributes() {
    return { threadId: { default: null, parseHTML: (el) => el.getAttribute('data-thread'), renderHTML: (a) => ({ 'data-thread': a.threadId }) } };
  },
  parseHTML() { return [{ tag: 'span[data-thread]' }]; },
  renderHTML({ HTMLAttributes, mark }) {
    const id = mark.attrs.threadId as string;
    return ['span', mergeAttributes(HTMLAttributes, { class: 'comment-mark', 'data-resolved': this.options.resolved[id] ? 'true' : 'false' }), 0];
  },
  addCommands() {
    return {
      setComment: (threadId: string) => ({ commands }) => commands.setMark(this.name, { threadId }),
    };
  },
});

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    comment: { setComment: (threadId: string) => ReturnType };
  }
}

/**
 * The face a run is actually set in, carried verbatim so it survives the round trip through the
 * editor. Bold and italic can only say "bolder than the paragraph", so a run lighter than its
 * paragraph (a Light run in a Bold paragraph) had no mark to travel in and came back bold.
 *
 * Nothing sets this mark by hand: it is applied when a story is seeded and inherited by text typed
 * inside a run, which is the whole point. The panel draws the face too, so a light run reads light
 * rather than taking the paragraph style's weight.
 */
export const FaceMark = Mark.create({
  name: 'face',
  addAttributes() {
    return { fontId: { default: null, parseHTML: (el) => el.getAttribute('data-face'), renderHTML: (a) => (a.fontId ? { 'data-face': a.fontId } : {}) } };
  },
  parseHTML() { return [{ tag: 'span[data-face]' }]; },
  renderHTML({ HTMLAttributes, mark }) {
    const fontId = mark.attrs.fontId as string | null;
    const style = fontId ? `font-family:"${fontId}","Helvetica Neue",Helvetica,sans-serif` : '';
    return ['span', mergeAttributes(HTMLAttributes, style ? { style } : {}), 0];
  },
});

/** InDesign character style applied to a range. */
export const CharStyleMark = Mark.create({
  name: 'charStyle',
  addAttributes() {
    return { id: { default: null, parseHTML: (el) => el.getAttribute('data-char-style'), renderHTML: (a) => ({ 'data-char-style': a.id }) } };
  },
  parseHTML() { return [{ tag: 'span[data-char-style]' }]; },
  renderHTML({ HTMLAttributes }) { return ['span', HTMLAttributes, 0]; },
});

const identityKey = new PluginKey('paragraphIdentity');
const caretKey = new PluginKey('caretParagraph');

/**
 * Keeps every paragraph uniquely identified: a split duplicates attrs (same paraId twice) and a
 * new block has none, so both get `new:<uuid>` and inherit the style of the paragraph before.
 */
export const ParagraphIdentity = Extension.create({
  name: 'paragraphIdentity',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: identityKey,
        appendTransaction(transactions, _old, state) {
          if (!transactions.some((t) => t.docChanged)) return null;
          const seen = new Set<string>();
          let tr = state.tr;
          let changed = false;
          let prevStyle: string | null = null;
          state.doc.forEach((node, offset) => {
            if (node.type.name !== 'paragraph') return;
            const attrs = { ...node.attrs } as { paraId: string | null; styleId: string | null };
            let dirty = false;
            if (!attrs.paraId || seen.has(attrs.paraId)) { attrs.paraId = mintParaId(); dirty = true; }
            if (!attrs.styleId && prevStyle) { attrs.styleId = prevStyle; dirty = true; }
            seen.add(attrs.paraId);
            prevStyle = attrs.styleId;
            if (dirty) { tr = tr.setNodeMarkup(offset, undefined, attrs); changed = true; }
          });
          return changed ? tr : null;
        },
      }),
      new Plugin({
        key: caretKey,
        props: {
          decorations(state) {
            const $head = state.selection.$head;
            if ($head.depth < 1) return DecorationSet.empty;
            const start = $head.before(1);
            const node = state.doc.nodeAt(start);
            if (!node) return DecorationSet.empty;
            return DecorationSet.create(state.doc, [Decoration.node(start, start + node.nodeSize, { class: 'caret-para' })]);
          },
        },
      }),
    ];
  },
});
